import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X, Shield, ShieldAlert, ShieldCheck, AlertTriangle, Info } from "lucide-react";
import type { ScanIssue } from "../../types";

interface SecurityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  scanStatus: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
  scanIssues: ScanIssue[];
  scanFindings?: { type: string; count: number }[];
  lastScanDate?: string | null;
  scanCount?: number;
}

const SEVERITY_CONFIG = {
  HIGH: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "High",
    icon: ShieldAlert,
  },
  MEDIUM: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    label: "Medium",
    icon: AlertTriangle,
  },
  LOW: {
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    label: "Low",
    icon: Info,
  },
};

const STATUS_CONFIG = {
  clean: {
    color: "#22c55e",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    label: "Clean",
    icon: ShieldCheck,
  },
  flagged: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    label: "Flagged",
    icon: Shield,
  },
  blocked: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "Blocked",
    icon: ShieldAlert,
  },
  not_scanned: {
    color: "#6b7280",
    bg: "#f3f4f6",
    border: "#e5e7eb",
    label: "Not Scanned",
    icon: Shield,
  },
  pending: {
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    label: "Scan Pending",
    icon: Info,
  },
  failed: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "Scan Failed",
    icon: ShieldAlert,
  },
};

const getFriendlyLabel = (type: string) => {
  const map: Record<string, string> = {
    'CREDIT_DEBIT_CARD_NUMBER': 'Credit Card',
    'CREDIT_DEBIT_CARD_EXPIRY': 'Credit Card Expiry',
    'CREDIT_DEBIT_CARD_CVV': 'Credit Card CVV',
    'EMAIL_ADDRESS': 'Email Address',
    'PHONE_NUMBER': 'Phone Number',
    'PASSWORD': 'Password',
    'AWS_ACCESS_KEY': 'AWS Access Key',
    'AWS_SECRET_KEY': 'AWS Secret Key'
  };
  return map[type] || type;
};

const SecurityReportModal: React.FC<SecurityReportModalProps> = ({
  isOpen,
  onClose,
  fileName,
  scanStatus,
  scanIssues = [],
  scanFindings = [],
  lastScanDate,
  scanCount = 0,
}) => {
  if (!isOpen) return null;

  const statusInfo = STATUS_CONFIG[scanStatus] || STATUS_CONFIG.clean;
  const StatusIcon = statusInfo.icon;

  // Group issues by severity
  const groupedIssues: Record<string, ScanIssue[]> = { HIGH: [], MEDIUM: [], LOW: [] };
  scanIssues.forEach((issue) => {
    const sev = issue.severity || "MEDIUM";
    if (!groupedIssues[sev]) groupedIssues[sev] = [];
    groupedIssues[sev].push(issue);
  });

  const severityOrder: ("HIGH" | "MEDIUM" | "LOW")[] = ["HIGH", "MEDIUM", "LOW"];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative w-full max-w-[460px] mx-4 overflow-hidden text-left align-middle transition-all transform"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, ${statusInfo.bg}, #ffffff)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: statusInfo.bg,
                border: `2px solid ${statusInfo.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StatusIcon size={20} color={statusInfo.color} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                Security Scan Report
              </h2>
              <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                {fileName}
              </p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {/* Status Banner */}
        {scanIssues.length > 0 && (
          <div
            style={{
              margin: "16px 24px 0",
              padding: "12px 16px",
              borderRadius: "10px",
              backgroundColor: statusInfo.bg,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <StatusIcon size={18} color={statusInfo.color} />
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: statusInfo.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {statusInfo.label}
              </span>
              <span style={{ fontSize: "13px", color: statusInfo.color, marginLeft: "8px" }}>
                {scanIssues.length} finding{scanIssues.length > 1 ? "s" : ""} detected.
              </span>
            </div>
          </div>
        )}

        {/* Issues List */}
        <div
          style={{
            padding: "16px 24px 24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {scanIssues.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#9ca3af",
              }}
            >
              {scanStatus === "not_scanned" ? (
                <>
                  <Shield size={48} color="#6b7280" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontSize: "14px", fontWeight: 500, color: "#4b5563" }}>
                    Not Scanned
                  </p>
                  <p style={{ fontSize: "12px" }}>
                    This file was uploaded without security scanning.
                  </p>
                </>
              ) : scanStatus === "pending" ? (
                <>
                  <Info size={48} color="#3b82f6" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontSize: "14px", fontWeight: 500, color: "#4b5563" }}>
                    Scan Pending
                  </p>
                  <p style={{ fontSize: "12px" }}>
                    This file is currently queued for security scanning. Please check back later.
                  </p>
                </>
              ) : (
                <>
                  <ShieldCheck size={48} color="#22c55e" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontSize: "14px", fontWeight: 500, color: "#4b5563" }}>
                    No issues found
                  </p>
                  <p style={{ fontSize: "12px" }}>
                    This file passed all security checks.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {severityOrder.map((sev) => {
                const issues = groupedIssues[sev];
                if (!issues || issues.length === 0) return null;
                const sevConfig = SEVERITY_CONFIG[sev];

                return (
                  <div key={sev}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                      color: sevConfig.color,
                      fontSize: "12px",
                      fontWeight: 700,
                    }}>
                      <sevConfig.icon size={16} />
                      {sev} SEVERITY ({issues.length})
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {issues.map((issue, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "8px",
                            borderRadius: "6px",
                            border: `1px solid ${sevConfig.border}`,
                            backgroundColor: sevConfig.bg,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>
                              {getFriendlyLabel(issue.label || issue.type)}
                            </span>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor: issue.action === "BLOCK" ? "#ef4444" : "#3b82f6",
                              color: "white",
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase"
                            }}>
                              {issue.action || "BLOCKED"}
                            </span>
                          </div>

                          <div style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: "13px",
                            color: "#334155",
                            marginBottom: "8px"
                          }}>
                            {issue.redacted_value || "****"}
                          </div>
                          
                          <div style={{ display: "flex", gap: "6px", fontSize: "11px", color: "#475569" }}>
                            <span style={{ backgroundColor: "#f3f4f6", padding: "3px 6px", borderRadius: "4px" }}>
                              Source: {issue.source || "BEDROCK"}
                            </span>
                            <span style={{ backgroundColor: "#f1f5f9", padding: "3px 6px", borderRadius: "4px" }}>
                              Type: {issue.type || "PII"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SecurityReportModal;
